import {
	getHomeExperiencePolicy,
	type HomeExperiencePolicyOutput,
	type ScreenExperienceInputs,
	type TierId,
	type TierSource,
	type VisualLayer,
} from "@dramadetector/shared";

import {
	useCrisisAssistanceFlag,
	useGenerativeResultsFlag,
	useSignupVariantFlag,
	useRewritesEnabledFlag,
	useSduiRenderingFlag,
	useSharedLinksFlag,
	useStreamingResultsFlag,
	useTrialPromptsFlag,
} from "./useFeatureFlags";

interface UseHomeExperiencePolicyArgs {
	accessToken: string | null;
	userId: string | null;
	isAnonymous: boolean;
	isRealUser: boolean;
	authLoading: boolean;
	tier: TierId;
	tierLevel: 1 | 2 | 3;
	visualLayer: VisualLayer;
	source: TierSource;
	isPremium: boolean;
	isEntitlementsLoading: boolean;
	analysisCount: number | null;
	isTrialStateLoaded: boolean;
	hasAnalysis: boolean;
	errorCode?: string | null;
}

function deriveVisitState(
	isAnonymous: boolean,
	isTrialStateLoaded: boolean,
	analysisCount: number | null,
): ScreenExperienceInputs["trial"]["visitState"] {
	if (!isAnonymous) return "returning";
	if (!isTrialStateLoaded) return "unknown";
	return analysisCount === 0 ? "new" : "returning";
}

function deriveLifecycle(
	authLoading: boolean,
	isEntitlementsLoading: boolean,
	isTrialStateLoaded: boolean,
	errorCode: string | null,
): ScreenExperienceInputs["data"]["lifecycle"] {
	if (authLoading || isEntitlementsLoading || !isTrialStateLoaded)
		return "loading";
	if (errorCode) return "error";
	return "ready";
}

export function useHomeExperiencePolicy({
	accessToken,
	userId,
	isAnonymous,
	isRealUser,
	authLoading,
	tier,
	tierLevel,
	visualLayer,
	source,
	isPremium,
	isEntitlementsLoading,
	analysisCount,
	isTrialStateLoaded,
	hasAnalysis,
	errorCode = null,
}: UseHomeExperiencePolicyArgs): HomeExperiencePolicyOutput {
	const signupVariant = useSignupVariantFlag();
	const areFlagsLoading = false;
	const sharedLinksEnabled = useSharedLinksFlag();
	const generativeResultsEnabled = useGenerativeResultsFlag();
	const sduiRenderingEnabled = useSduiRenderingFlag();
	const rewritesEnabled = useRewritesEnabledFlag();
	const crisisAssistanceEnabled = useCrisisAssistanceFlag();
	const streamingResultsEnabled = useStreamingResultsFlag();
	const trialPromptsEnabled = useTrialPromptsFlag();

	const visitState = deriveVisitState(
		isAnonymous,
		isTrialStateLoaded,
		analysisCount,
	);
	const lifecycle = deriveLifecycle(
		authLoading,
		isEntitlementsLoading,
		isTrialStateLoaded,
		errorCode,
	);

	return getHomeExperiencePolicy({
		surface: "home",
		auth: {
			identity: isAnonymous ? "anonymous" : "authenticated",
			isAnonymous,
			isRealUser,
			userId,
			hasAccessToken: Boolean(accessToken),
			isLoading: authLoading,
		},
		entitlements: {
			tier,
			tierLevel,
			visualLayer,
			source,
			isPremium,
			isLoading: isEntitlementsLoading,
		},
		trial: {
			analysisCount,
			visitState,
			isLoaded: isTrialStateLoaded,
		},
		features: {
			isFlagsReady: !areFlagsLoading,
			treatment: signupVariant === "optimized" ? "treatment" : "control",
			treatmentKey: signupVariant,
			signupVariant,
			sharedLinksEnabled,
			generativeResultsEnabled,
			sduiRenderingEnabled,
			rewritesEnabled,
			crisisAssistanceEnabled,
			streamingResultsEnabled,
			trialPromptsEnabled,
			conversionUxEnabled: false,
		},
		data: {
			lifecycle,
			hasAnalysis,
			hasRemoteConfig: false,
			hasSharedLinkPayload: false,
			errorCode,
		},
	});
}
